import { Controller } from "@hotwired/stimulus"

// Provides live update announcements and visual feedback for Turbo Stream updates
export default class extends Controller {
  static targets = ["announcement"]

  connect() {
    // Listen for Turbo Stream render events
    document.addEventListener("turbo:before-stream-render", this.handleStreamRender.bind(this))

    // Initialize throttle queue
    this.updateQueue = []
    this.throttleDelay = 500 // milliseconds
    this.isProcessing = false
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.handleStreamRender.bind(this))
  }

  handleStreamRender(event) {
    const { target } = event.detail.newStream

    if (!target) return

    const updateType = this.detectUpdateType(target.id)

    if (updateType) {
      this.queueUpdate(updateType, target)
    }
  }

  detectUpdateType(targetId) {
    if (!targetId) return null

    // Detect card updates
    if (targetId.match(/^card_\d+$/)) {
      return "card"
    }

    // Detect step updates
    if (targetId.match(/^step_\d+$/)) {
      return "step"
    }

    return null
  }

  queueUpdate(updateType, targetElement) {
    // Add to queue
    this.updateQueue.push({ updateType, targetElement })

    // Process queue if not already processing
    if (!this.isProcessing) {
      this.processQueue()
    }
  }

  async processQueue() {
    if (this.updateQueue.length === 0) {
      this.isProcessing = false
      return
    }

    this.isProcessing = true
    const update = this.updateQueue.shift()

    // Apply visual animation
    this.applyUpdateAnimation(update.targetElement)

    // Announce to screen readers
    this.announceUpdate(update.updateType)

    // Wait for throttle delay before processing next update
    await new Promise(resolve => setTimeout(resolve, this.throttleDelay))

    this.processQueue()
  }

  applyUpdateAnimation(targetElement) {
    // Check if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    if (prefersReducedMotion) {
      // Skip animation if user prefers reduced motion
      return
    }

    // Find the actual turbo-frame element if targetElement is a template
    const frameId = targetElement.id
    const frame = document.getElementById(frameId)

    if (frame) {
      // Add pulse animation class
      frame.classList.add("pulse-once")

      // Remove class after animation completes
      setTimeout(() => {
        frame.classList.remove("pulse-once")
      }, 600) // Animation duration
    }
  }

  announceUpdate(updateType) {
    if (!this.hasAnnouncementTarget) return

    const messages = {
      card: "Card updated",
      step: "Step updated"
    }

    const message = messages[updateType] || "Content updated"

    // Update ARIA live region
    this.announcementTarget.textContent = message

    // Clear announcement after screen reader has time to read it
    setTimeout(() => {
      if (this.hasAnnouncementTarget) {
        this.announcementTarget.textContent = ""
      }
    }, 2000)
  }
}
